import { EchelonPriority } from '../../../common/constants';
import { UpsertEntityInput } from '../../../types/graphql-global-types';
import {
  OptionsGrouped,
  OptionType,
} from '../../../common/components/CustomSelect/CustomSelect';
import { GetEchelon_getEchelon } from '../../../graphql/echelon/types/GetEchelon';
import { insert } from '../../../common/logics';

type InputType = {
  type: 'text' | 'email' | 'select' | 'tel' | 'autoComplete';
  label: string;
  value: any;
  key: keyof UpsertEntityInput;
  options?: OptionType[];
  optionsGrouped?: OptionsGrouped[];
  required?: boolean;
  helperText?: string;
};

type EntityInputsType = {
  entity: UpsertEntityInput;
  echelon: GetEchelon_getEchelon | null | undefined;
  enseignOptions: OptionType[];
  echelonOptions: OptionType[];
  parentEntitiesOptions: OptionType[];
  reseauOptions: OptionType[];
};

export const useEntityInputs = ({
  entity,
  echelon,
  enseignOptions,
  echelonOptions,
  parentEntitiesOptions,
  reseauOptions,
}: EntityInputsType) => {
  let inputs: InputType[] = [
    {
      type: 'text',
      label: 'Nom',
      value: entity.name,
      key: 'name',
      required: true,
      helperText: 'Veuillez entrer un nom',
    },
    {
      type: 'text',
      label: 'Adresse',
      value: entity.adress,
      key: 'adress',
      required: !(
        echelon?.priority === EchelonPriority.EQUIPE ||
        echelon?.priority === EchelonPriority.RESEAU
      ),
      helperText: 'Veuillez entrer une adresse',
    },
    {
      type: 'text',
      label: 'Code postal',
      value: entity.zipCode,
      key: 'zipCode',
      required: !(
        echelon?.priority === EchelonPriority.EQUIPE ||
        echelon?.priority === EchelonPriority.RESEAU
      ),
      helperText: 'Veuillez entrer un code postal',
    },
    {
      type: 'text',
      label: 'Ville',
      value: entity.town,
      key: 'town',
      required: !(
        echelon?.priority === EchelonPriority.EQUIPE ||
        echelon?.priority === EchelonPriority.RESEAU
      ),
      helperText: 'Veuillez entrer un nom de ville',
    },
    {
      type: 'email',
      label: 'Email',
      value: entity.email,
      key: 'email',
      required: false,
      helperText: 'Veuillez entrer une adresse email valide',
    },
    {
      type: 'tel',
      label: 'Téléphone',
      value: entity.phone,
      key: 'phone',
      required: false,
      helperText: 'Veuillez entrer un numéro de téléphone',
    },
    {
      type: 'text',
      label: 'SIRET',
      value: entity.siret,
      key: 'siret',
      required: false,
      helperText: 'Numéro de SIRET invalide',
    },
    {
      type: 'select',
      label: 'Enseigne',
      value: entity.enseigneId,
      key: 'enseigneId',
      options: enseignOptions,
      required: echelon?.priority !== EchelonPriority.RESEAU,
      helperText: 'Veuillez choisir une enseigne',
    },
    {
      type: 'select',
      label: 'Echelon',
      value: entity.echelonId,
      key: 'echelonId',
      options: echelonOptions,
      required: true,
      helperText: 'Veuillez choisir un échelon',
    },
    {
      type: 'select',
      label: 'Entité parente',
      value: entity.parentEntityId,
      key: 'parentEntityId',
      options: parentEntitiesOptions.sort(
        (a, b) =>
          -(a.groupeLabel as string).localeCompare(b?.groupeLabel as string),
      ),
    },
  ];

  if (echelon && echelon?.priority !== EchelonPriority.RESEAU) {
    inputs = insert(inputs, inputs.length, {
      type: 'select',
      label: 'Réseau (cross-enseignes)',
      value: entity.reseauId,
      key: 'reseauId',
      options: reseauOptions,
    });
  }

  if (echelon && echelon?.priority === EchelonPriority.RESEAU) {
    inputs = inputs.filter((input) => input.key !== 'enseigneId');
  }

  if (entity?.echelonId && echelon?.priority === EchelonPriority.EQUIPE) {
    inputs = inputs.filter((input) => input.key !== 'siret');
  }

  return inputs;
};
